import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../components/AppToast";
import { getallfacility } from "../../services/facility/facility";
import {
  buildStockTransferInPayload,
  createStockTransferIn,
  isShipmentEmpty,
  parseRefStoFromRemarks,
  pickShipmentFields,
  unwrapStockTransferInDocument,
} from "../../services/stockTransferIn/stockTransferIn";
import {
  extractStockTransferOutId,
  fetchAllStockTransferOutListByDestination,
  getStockTransferOutById,
} from "../../services/stockTransferOut/stockTransferOut";
import { formatToInputDate } from "../../utils/dateFormatter";
import "./add-stock-transfer-out.css";

const FACILITY_LIST_PAGE_SIZE = 500;

function formatFacilityAddress(facility) {
  if (!facility) return "";
  const addr = facility.Facility_Address || {};
  const line1 = [addr.address1, addr.address2, addr.address3, addr.address4].filter(Boolean).join(", ");
  const line2 = [addr.city, addr.state, addr.postalCode || addr.zip || addr.postal].filter(Boolean).join(", ");
  const line3 = addr.country || "";
  return [line1, line2, line3].filter(Boolean).join("\n");
}

function resolveStoFacilityMongoId(doc, facilities, role) {
  if (!doc || !Array.isArray(facilities)) return "";
  const idField = role === "source" ? "sourceLocationId" : "destinationLocationId";
  const codeField = role === "source" ? "sourceLocation" : "destinationLocation";
  const mongo = doc[idField];
  if (mongo && facilities.some((f) => f._id === mongo)) return mongo;

  const code = doc[codeField];
  if (code != null && String(code).trim()) {
    const s = String(code).trim();
    const byCode = facilities.find((f) => f.Facility_ID === s || f._id === s);
    if (byCode) return byCode._id;
  }

  const nameHint = doc[codeField];
  if (typeof nameHint === "string" && nameHint.trim()) {
    const byName = facilities.find(
      (f) => (f.Facility_Name || "").trim().toLowerCase() === nameHint.trim().toLowerCase()
    );
    if (byName) return byName._id;
  }

  return "";
}

function newLineId(suffix) {
  return `item-${Date.now()}-${suffix}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Same line total as Stock Transfer Out: qty × unit price × (1 + tax%). */
const calculateItemTotal = ({ receivedQty, unitPrice, tax }) => {
  const qty = Number(receivedQty) || 0;
  const price = Number(unitPrice) || 0;
  const taxPct = Number(tax) || 0;
  const subtotal = qty * price;
  return Number((subtotal * (1 + taxPct / 100)).toFixed(2));
};

/** Map saved STI / STO line items into table rows (received qty column). */
function mapDocumentLinesToStiItems(lineItems) {
  const arr = Array.isArray(lineItems) ? lineItems : [];
  return arr.map((item, idx) => {
    const rawLine = item.lineNo ?? idx + 1;
    const lineNo = typeof rawLine === "number" ? rawLine : Number(rawLine) || idx + 1;
    const receivedQty =
      item.orderedQty ?? item.receivedQty ?? item.transferQty ?? item.qty ?? 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const tax = Number(item.tax) || 0;
    const availableQty = item.availableQty ?? item.availQty ?? "";
    const qcRequired =
      item.qcRequired != null && item.qcRequired !== "" ? String(item.qcRequired) : "No";
    const itemCube =
      item.itemCube === undefined || item.itemCube === null ? "" : String(item.itemCube);
    const itemWeight =
      item.itemWeight === undefined || item.itemWeight === null ? "" : String(item.itemWeight);
    const row = { receivedQty, unitPrice, tax };
    return {
      id: newLineId(`${idx}-${lineNo}`),
      lineNo,
      skuId: item.skuId ?? item.sku ?? "",
      description: item.description ?? "",
      pack: item.pack ?? "STD",
      uom: item.uom ?? "EA",
      availableQty: availableQty === "" ? "" : String(availableQty),
      qcRequired,
      itemCube,
      itemWeight,
      receivedQty,
      unitPrice,
      tax,
      totalAmount: calculateItemTotal(row),
    };
  });
}

function mapStoLinesToStiItems(stoItems) {
  const arr = Array.isArray(stoItems) ? stoItems : [];
  return arr.map((item, idx) => {
    const rawLine = item.lineNo ?? idx + 1;
    const lineNo = typeof rawLine === "number" ? rawLine : Number(rawLine) || idx + 1;
    const receivedQty = item.orderedQty ?? item.transferQty ?? item.qty ?? 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const tax = Number(item.tax) || 0;
    const availableQty = item.availableQty ?? item.availQty ?? "";
    const qcRequired = item.qcRequired != null && item.qcRequired !== "" ? String(item.qcRequired) : "No";
    const itemCube =
      item.itemCube === undefined || item.itemCube === null ? "" : String(item.itemCube);
    const itemWeight =
      item.itemWeight === undefined || item.itemWeight === null ? "" : String(item.itemWeight);
    const row = {
      receivedQty,
      unitPrice,
      tax,
    };
    return {
      id: newLineId(`${idx}-${lineNo}`),
      lineNo,
      skuId: item.skuId ?? item.sku ?? "",
      description: item.description ?? "",
      pack: item.pack ?? "STD",
      uom: item.uom ?? "EA",
      availableQty: availableQty === "" ? "" : String(availableQty),
      qcRequired,
      itemCube,
      itemWeight,
      receivedQty,
      unitPrice,
      tax,
      totalAmount: calculateItemTotal(row),
    };
  });
}

function applyShipmentToState(doc, setters) {
  const shipment = pickShipmentFields(doc);
  setters.setProductWeightGrams(shipment.productWeightGrams);
  setters.setTransporterName(shipment.transporterName);
  setters.setNoOfBoxes(shipment.noOfBoxes);
  setters.setGrossWeight(shipment.grossWeight);
  return shipment;
}

function stoDropdownLabel(row) {
  const id = extractStockTransferOutId(row);
  const no = String(row?.transferNo || "").trim();
  if (no && no !== "—") return no;
  if (id) return `STO (${String(id).slice(-8)})`;
  return "—";
}

export default function AddStockTransferIn({
  onCancel,
  viewOnly = false,
  initialDocument = null,
}) {
  const selectedFacility = useSelector((state) => state?.facility?.selectedFacility);
  const skipFacilityAddressSyncRef = useRef({ source: false, dest: false });

  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [stoOptions, setStoOptions] = useState([]);
  const [stoListLoading, setStoListLoading] = useState(false);
  const [selectedStoId, setSelectedStoId] = useState("");
  const [stoDetailLoading, setStoDetailLoading] = useState(false);

  const [sourceLocation, setSourceLocation] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  /** From STO API when mongo id does not match facility master (payload / display fallback). */
  const [sourceLocationCode, setSourceLocationCode] = useState("");
  const [destinationLocationCode, setDestinationLocationCode] = useState("");
  const [sourceAddress, setSourceAddress] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [referenceStoNo, setReferenceStoNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [stoExpectedDeliveryIso, setStoExpectedDeliveryIso] = useState("");
  const [productWeightGrams, setProductWeightGrams] = useState("");
  const [transporterName, setTransporterName] = useState("");
  const [noOfBoxes, setNoOfBoxes] = useState("");
  const [grossWeight, setGrossWeight] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [transferInNo, setTransferInNo] = useState("");
  const [recordStatus, setRecordStatus] = useState("");

  useEffect(() => {
    if (viewOnly && initialDocument) return;
    let cancelled = false;
    (async () => {
      setFacilitiesLoading(true);
      try {
        const res = await getallfacility(0, FACILITY_LIST_PAGE_SIZE);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (cancelled) return;
        setFacilities(list);
      } catch (err) {
        if (!cancelled) {
          console.error("facility/fetch failed", err);
          toast.error(typeof err === "string" ? err : "Failed to load facilities");
          setFacilities([]);
        }
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOnly, initialDocument]);

  useEffect(() => {
    if (!viewOnly || !initialDocument) return;
    const doc = unwrapStockTransferInDocument(initialDocument);
    applyShipmentToState(doc, {
      setProductWeightGrams,
      setTransporterName,
      setNoOfBoxes,
      setGrossWeight,
    });

    let cancelled = false;
    (async () => {
      setFacilitiesLoading(true);
      try {
        const res = await getallfacility(0, FACILITY_LIST_PAGE_SIZE);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (cancelled) return;
        setFacilities(list);
        const src = resolveStoFacilityMongoId(doc, list, "source");
        const dst = resolveStoFacilityMongoId(doc, list, "destination");
        const srcFac = src ? list.find((f) => f._id === src) : null;
        const dstFac = dst ? list.find((f) => f._id === dst) : null;

        skipFacilityAddressSyncRef.current = { source: true, dest: true };
        setSourceLocation(src || "");
        setDestinationLocation(dst || "");
        setSourceLocationCode(
          String(srcFac?.Facility_ID || doc.sourceLocation || "").trim()
        );
        setDestinationLocationCode(
          String(dstFac?.Facility_ID || doc.destinationLocation || "").trim()
        );
        setSourceAddress(
          (doc.sourceAddress && String(doc.sourceAddress)) ||
            (srcFac ? formatFacilityAddress(srcFac) : "") ||
            ""
        );
        setDestinationAddress(
          (doc.destinationAddress && String(doc.destinationAddress)) ||
            (dstFac ? formatFacilityAddress(dstFac) : "") ||
            ""
        );
        setTransferInNo(
          String(doc.transferNumber ?? doc.transferInNo ?? doc.transferInNumber ?? "").trim()
        );
        setReferenceStoNo(parseRefStoFromRemarks(doc.remarks));
        setRecordStatus(String(doc.status ?? "").trim());
        setRemarks(String(doc.remarks ?? "").trim());
        setReceivedDate(
          formatToInputDate(doc.expectedDeliveryDate ?? doc.expected_delivery_date, false) ||
            ""
        );
        setStoExpectedDeliveryIso(
          formatToInputDate(doc.expectedDeliveryDate ?? doc.expected_delivery_date, false) ||
            ""
        );
        let shipment = pickShipmentFields(doc);
        if (isShipmentEmpty(shipment)) {
          const refSto = parseRefStoFromRemarks(doc.remarks);
          if (refSto) {
            try {
              const stoRows = await fetchAllStockTransferOutListByDestination({
                limit: 100,
                status: "all",
                maxPages: 50,
                destinationLocationId: selectedFacility?.id || undefined,
              });
              const stoRow = (stoRows || []).find((row) => {
                const no = String(
                  row.transferNo ?? row.transferNumber ?? row.stoNumber ?? ""
                ).trim();
                return no === refSto;
              });
              const stoId = stoRow ? extractStockTransferOutId(stoRow) : "";
              if (stoId) {
                const stoDoc = await getStockTransferOutById(stoId);
                shipment = pickShipmentFields(stoDoc);
              }
            } catch {
              /* optional STO shipment fallback */
            }
          }
        }
        if (!cancelled && !isShipmentEmpty(shipment)) {
          setProductWeightGrams(shipment.productWeightGrams);
          setTransporterName(shipment.transporterName);
          setNoOfBoxes(shipment.noOfBoxes);
          setGrossWeight(shipment.grossWeight);
        }
        setItems(mapDocumentLinesToStiItems(doc.items ?? doc.lineItems));
        setSelectedStoId("");
      } catch (err) {
        if (!cancelled) {
          console.error("facility/fetch failed (view)", err);
          toast.error(typeof err === "string" ? err : "Failed to load facilities");
          setFacilities([]);
        }
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOnly, initialDocument, selectedFacility?.id]);

  useEffect(() => {
    if (viewOnly) return;
    let cancelled = false;
    (async () => {
      setStoListLoading(true);
      try {
        const rows = await fetchAllStockTransferOutListByDestination({
          limit: 100,
          status: "all",
          maxPages: 100,
          destinationLocationId: selectedFacility?.id || undefined,
        });
        if (cancelled) return;
        setStoOptions((rows || []).filter((r) => extractStockTransferOutId(r)));
      } catch (err) {
        if (!cancelled) {
          console.error("stock-transfer-out list failed", err);
          toast.error(err?.message || "Failed to load stock transfer out list");
          setStoOptions([]);
        }
      } finally {
        if (!cancelled) setStoListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOnly, selectedFacility?.id]);

  useEffect(() => {
    if (viewOnly) return;
    if (!sourceLocation || facilities.length === 0) return;
    if (skipFacilityAddressSyncRef.current.source) {
      skipFacilityAddressSyncRef.current.source = false;
      return;
    }
    const selected = facilities.find((f) => f._id === sourceLocation);
    if (selected) setSourceAddress(formatFacilityAddress(selected));
  }, [viewOnly, sourceLocation, facilities]);

  useEffect(() => {
    if (viewOnly) return;
    if (!destinationLocation || facilities.length === 0) return;
    if (skipFacilityAddressSyncRef.current.dest) {
      skipFacilityAddressSyncRef.current.dest = false;
      return;
    }
    const selected = facilities.find((f) => f._id === destinationLocation);
    if (selected) setDestinationAddress(formatFacilityAddress(selected));
  }, [viewOnly, destinationLocation, facilities]);

  const resetFormToEmpty = useCallback(() => {
    setReferenceStoNo("");
    setRemarks("");
    setReceivedDate("");
    setStoExpectedDeliveryIso("");
    setProductWeightGrams("");
    setTransporterName("");
    setNoOfBoxes("");
    setGrossWeight("");
    setItems([]);
    setSourceLocation("");
    setDestinationLocation("");
    setSourceLocationCode("");
    setDestinationLocationCode("");
    setSourceAddress("");
    setDestinationAddress("");
  }, []);

  const applyStoDocument = useCallback(
    async (mongoId) => {
      setStoDetailLoading(true);
      try {
        const doc = await getStockTransferOutById(mongoId);
        const transferNoStr = String(
          doc.transferNo ?? doc.transferNumber ?? doc.stoNumber ?? doc.reference ?? ""
        ).trim();
        setReferenceStoNo(transferNoStr);

        const src = resolveStoFacilityMongoId(doc, facilities, "source");
        const dst = resolveStoFacilityMongoId(doc, facilities, "destination");
        const srcFac = src ? facilities.find((f) => f._id === src) : null;
        const dstFac = dst ? facilities.find((f) => f._id === dst) : null;

        const srcAddr =
          (doc.sourceAddress && String(doc.sourceAddress)) ||
          (srcFac ? formatFacilityAddress(srcFac) : "") ||
          "";
        const dstAddr =
          (doc.destinationAddress && String(doc.destinationAddress)) ||
          (dstFac ? formatFacilityAddress(dstFac) : "") ||
          "";

        skipFacilityAddressSyncRef.current = { source: true, dest: true };
        setSourceLocation(src || "");
        setDestinationLocation(dst || "");
        setSourceLocationCode(
          String(srcFac?.Facility_ID || doc.sourceLocation || doc.sourceFacilityId || "").trim()
        );
        setDestinationLocationCode(
          String(
            dstFac?.Facility_ID ||
              doc.destinationLocation ||
              doc.destinationFacilityId ||
              ""
          ).trim()
        );
        setSourceAddress(srcAddr);
        setDestinationAddress(dstAddr);

        setRemarks(doc.remarks != null ? String(doc.remarks) : "");

        const exp = doc.expectedDeliveryDate ?? doc.expectedDate;
        const expIso = formatToInputDate(exp, false) || "";
        setStoExpectedDeliveryIso(expIso);
        setReceivedDate(expIso || formatToInputDate(new Date(), false) || "");

        applyShipmentToState(doc, {
          setProductWeightGrams,
          setTransporterName,
          setNoOfBoxes,
          setGrossWeight,
        });
        const lines = mapStoLinesToStiItems(doc.items ?? doc.lineItems);
        setItems(lines);
      } catch (err) {
        toast.error(err?.message || "Failed to load stock transfer out");
        setSelectedStoId("");
        setReferenceStoNo("");
        setItems([]);
      } finally {
        setStoDetailLoading(false);
      }
    },
    [facilities]
  );

  const handleStoDropdownChange = async (e) => {
    const id = String(e.target.value || "").trim();
    setSelectedStoId(id);
    if (!id) {
      resetFormToEmpty();
      return;
    }
    if (facilitiesLoading) {
      toast.info("Wait for facilities to finish loading, then select the transfer again.");
      setSelectedStoId("");
      return;
    }
    await applyStoDocument(id);
  };

  const totals = useMemo(() => {
    const totalQty = items.reduce((sum, item) => sum + (Number(item.receivedQty) || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
    return { totalQty, totalAmount: Number(totalAmount.toFixed(2)) };
  }, [items]);

  const handleSave = async () => {
    if (viewOnly) return;
    if (saving) return;
    if (!selectedStoId) {
      toast.error("Select a Stock Transfer Out first.");
      return;
    }
    const hasSource =
      Boolean(sourceLocation) ||
      Boolean(String(sourceLocationCode || "").trim()) ||
      Boolean(String(sourceAddress || "").trim());
    const hasDest =
      Boolean(destinationLocation) ||
      Boolean(String(destinationLocationCode || "").trim()) ||
      Boolean(String(destinationAddress || "").trim());
    if (!hasSource) {
      toast.error("Ship-from location is missing from the selected transfer.");
      return;
    }
    if (!hasDest) {
      toast.error("Receiving location is missing from the selected transfer.");
      return;
    }
    if (items.length === 0) {
      toast.error("Select a Stock Transfer Out with line items, or ensure lines are loaded.");
      return;
    }

    const sourceFac = facilities.find((f) => f._id === sourceLocation);
    const destFac = facilities.find((f) => f._id === destinationLocation);

    const payload = buildStockTransferInPayload({
      sourceLocation: sourceFac?.Facility_ID || sourceLocationCode || sourceLocation,
      sourceLocationId: sourceFac?._id,
      sourceAddress,
      destinationLocation: destFac?.Facility_ID || destinationLocationCode || destinationLocation,
      destinationLocationId: destFac?._id,
      destinationAddress,
      totalTransferInQty: totals.totalQty,
      totalTransferInAmount: totals.totalAmount,
      remarks,
      referenceStoNo,
      receivedDate,
      stoExpectedDeliveryIso,
      productWeightGrams,
      transporterName,
      noOfBoxes,
      grossWeight,
      items,
      status: "CREATED",
    });

    setSaving(true);
    try {
      await createStockTransferIn(payload);
      toast.success("Stock Transfer In saved successfully");
      onCancel?.();
    } catch {
      /* validation / HTTP errors toasted by axios interceptor */
    } finally {
      setSaving(false);
    }
  };

  const stoDropdownDisabled =
    viewOnly || facilitiesLoading || stoListLoading || stoDetailLoading;
  const fieldReadOnly = viewOnly;

  const sourceLocationSelect = useMemo(() => {
    if (!viewOnly && !selectedStoId) {
      return (
        <option value="">Select a Stock Transfer Out first</option>
      );
    }
    if (viewOnly && !sourceLocation && !sourceLocationCode) {
      return <option value="">—</option>;
    }
    if (sourceLocation && facilities.some((f) => f._id === sourceLocation)) {
      const f = facilities.find((x) => x._id === sourceLocation);
      return (
        <option value={sourceLocation}>
          {f.Facility_Name || f.Facility_ID || sourceLocation}
          {f.Facility_ID ? ` (${f.Facility_ID})` : ""}
        </option>
      );
    }
    if (sourceLocation) {
      return (
        <option value={sourceLocation}>
          {sourceLocationCode || sourceLocation}
        </option>
      );
    }
    return (
      <option value="">
        {sourceLocationCode || "Ship-from not matched to facility list"}
      </option>
    );
  }, [viewOnly, selectedStoId, sourceLocation, sourceLocationCode, facilities]);

  const destinationLocationSelect = useMemo(() => {
    if (!viewOnly && !selectedStoId) {
      return (
        <option value="">Select a Stock Transfer Out first</option>
      );
    }
    if (viewOnly && !destinationLocation && !destinationLocationCode) {
      return <option value="">—</option>;
    }
    if (destinationLocation && facilities.some((f) => f._id === destinationLocation)) {
      const f = facilities.find((x) => x._id === destinationLocation);
      return (
        <option value={destinationLocation}>
          {f.Facility_Name || f.Facility_ID || destinationLocation}
          {f.Facility_ID ? ` (${f.Facility_ID})` : ""}
        </option>
      );
    }
    if (destinationLocation) {
      return (
        <option value={destinationLocation}>
          {destinationLocationCode || destinationLocation}
        </option>
      );
    }
    return (
      <option value="">
        {destinationLocationCode || "Receiving site not matched to facility list"}
      </option>
    );
  }, [viewOnly, selectedStoId, destinationLocation, destinationLocationCode, facilities]);

  const locationSelectValue = (loc) =>
    viewOnly || selectedStoId ? loc || "" : "";

  return (
    <div className="sto-create-root">
      <div className="mb-4">
        <h5 className="mb-3">
          {viewOnly ? "View Stock Transfer In" : "Create Stock Transfer In"}
        </h5>
        <div className="row g-3">
          {viewOnly && (
            <>
              <div className="col-md-4">
                <label className="form-label">Transfer In No.</label>
                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={transferInNo || "—"}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Status</label>
                <input
                  type="text"
                  className="form-control"
                  readOnly
                  value={recordStatus || "—"}
                />
              </div>
            </>
          )}
          <div className="col-md-4">
            <label className="form-label" htmlFor="sti-ref-sto">
              {viewOnly
                ? "Reference STO No."
                : "Reference STO No. (from Stock Transfer Out)"}
            </label>
            {viewOnly ? (
              <input
                id="sti-ref-sto"
                type="text"
                className="form-control"
                readOnly
                value={referenceStoNo || "—"}
              />
            ) : (
              <select
                id="sti-ref-sto"
                className="form-select"
                value={selectedStoId}
                onChange={handleStoDropdownChange}
                disabled={stoDropdownDisabled}
              >
                <option value="">
                  {stoListLoading ? "Loading Stock Transfer Out list…" : "— Select transfer —"}
                </option>
                {stoOptions.map((row) => {
                  const oid = extractStockTransferOutId(row);
                  if (!oid) return null;
                  return (
                    <option key={oid} value={oid}>
                      {stoDropdownLabel(row)}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">Source location (from Stock Transfer Out)</label>
            <select
              className="form-select"
              disabled
              value={locationSelectValue(sourceLocation)}
            >
              {facilitiesLoading ? (
                <option value="">Loading facilities…</option>
              ) : (
                sourceLocationSelect
              )}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Source address</label>
            <textarea readOnly className="form-control" rows={2} value={sourceAddress} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Destination location (from Stock Transfer Out)</label>
            <select
              className="form-select"
              disabled
              value={locationSelectValue(destinationLocation)}
            >
              {facilitiesLoading ? (
                <option value="">Loading facilities…</option>
              ) : (
                destinationLocationSelect
              )}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Destination address</label>
            <textarea readOnly className="form-control" rows={2} value={destinationAddress} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Expected delivery</label>
            <input
              type="date"
              className="form-control"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              readOnly={fieldReadOnly}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Total received qty</label>
            <input type="number" readOnly className="form-control" value={totals.totalQty} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Total received amount</label>
            <input type="number" readOnly className="form-control" value={totals.totalAmount} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-control"
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              readOnly={fieldReadOnly}
            />
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12">
            <h6 className="text-secondary small text-uppercase mb-2">Shipment (from Stock Transfer Out)</h6>
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="form-label">Product weight (g)</label>
            <input
              type="number"
              className="form-control"
              value={productWeightGrams}
              onChange={(e) => setProductWeightGrams(e.target.value)}
              min="0"
              step="1"
              readOnly={fieldReadOnly}
            />
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="form-label">Transporter / courier</label>
            <input
              type="text"
              className="form-control"
              value={transporterName}
              onChange={(e) => setTransporterName(e.target.value)}
              readOnly={fieldReadOnly}
            />
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="form-label">No. of boxes</label>
            <input
              type="number"
              className="form-control"
              value={noOfBoxes}
              onChange={(e) => setNoOfBoxes(e.target.value)}
              min="0"
              readOnly={fieldReadOnly}
            />
          </div>
          <div className="col-md-3 col-sm-6">
            <label className="form-label">Gross weight</label>
            <input
              type="number"
              className="form-control"
              value={grossWeight}
              onChange={(e) => setGrossWeight(e.target.value)}
              min="0"
              step="0.01"
              readOnly={fieldReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="sto-section">
        <div className="sto-section-header">
          <i className="bi bi-list-ul" aria-hidden />
          Item details
        </div>
        <div className="sto-section-body">
          <div className="sto-items-toolbar">
            <h6>
              <i className="bi bi-box-seam text-primary" aria-hidden />
              Line items
              <span className="badge bg-light text-dark border fw-normal ms-1">{items.length}</span>
            </h6>
          </div>

          <div className="sto-items-table-wrap mt-3">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>Line</th>
                  <th style={{ minWidth: 110 }}>SKU ID</th>
                  <th style={{ minWidth: 140 }}>Description</th>
                  <th style={{ width: 88 }}>Volume</th>
                  <th style={{ width: 88 }}>Weight</th>
                  <th style={{ width: 72 }}>UOM</th>
                  <th style={{ width: 80 }}>Pack</th>
                  <th style={{ width: 92 }}>QC</th>
                  <th style={{ width: 100 }}>Avail. qty</th>
                  <th style={{ width: 100 }}>Transfer qty</th>
                  <th style={{ width: 100 }}>Unit price</th>
                  <th style={{ width: 72 }}>Tax %</th>
                  <th style={{ width: 100 }}>Line total</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-muted text-center py-4 small">
                      {viewOnly
                        ? "No line items on this transfer in."
                        : (
                          <>
                            No line items to show. Select a <strong>Stock Transfer Out</strong> above;
                            line items load from that document and cannot be edited here.
                          </>
                        )}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="sto-line-badge">{item.lineNo}</span>
                      </td>
                      <td>{item.skuId}</td>
                      <td>{item.description}</td>
                      <td>{item.itemCube === "" || item.itemCube === undefined ? "—" : item.itemCube}</td>
                      <td>
                        {item.itemWeight === "" || item.itemWeight === undefined ? "—" : item.itemWeight}
                      </td>
                      <td>{item.uom}</td>
                      <td>{item.pack}</td>
                      <td>{item.qcRequired ?? "—"}</td>
                      <td>{item.availableQty === "" ? "—" : item.availableQty}</td>
                      <td>{item.receivedQty}</td>
                      <td>{Number(item.unitPrice).toFixed(2)}</td>
                      <td>{item.tax}</td>
                      <td className="font-monospace">
                        {(Number(item.totalAmount) || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sto-footer-actions d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {viewOnly ? "Back" : "Cancel"}
        </button>
        {!viewOnly && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || stoDetailLoading || facilitiesLoading}
          >
            {saving ? "Saving…" : "Save Stock Transfer In"}
          </button>
        )}
      </div>
    </div>
  );
}
